import { createAsyncThunk } from "@reduxjs/toolkit";
import userService from "@userService";
import ShowToaster from "@/toaster";
import {
  setSegmentsCatalog,
  setLoading,
  setError,
  optimisticInsertSegment,
  optimisticRemoveSegment,
  optimisticUpdateSegment,
  optimisticInsertCategory,
  optimisticUpdateCategory,
  optimisticRemoveCategory,
} from "./segmentsSlice";
import { normaliseSegmentError } from "@/store/slices/segments/errors";
import { selectAppliedSegmentCodes } from "./selectors";
import { selectSmartLink, setLinkSegments } from "@/store/slices/smartLink/smartLinkSlice";
import { resetSegmentsReady } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import type {
  Segment as SegmentV2,
  SegmentListResponse,
  SegmentPatchRequest,
  SegmentCategory,
} from "@interfaces/segments/domain";
import { toSegmentsFilterInputs } from "@utils/filters/adapters";
import type { FilterSelection } from "@interfaces/filters/domain";
import type { AppDispatch, RootState } from "@/store/store";

// Module-level generation counter and abort controller for segment reorder
// batches — a newer reorder supersedes (aborts) an older in-flight one, and
// only the latest generation is allowed to commit its result to the slice.
let segmentsReorderGeneration = 0;
let segmentsReorderAbort: AbortController | null = null;

// Generation counters for catalog fetches, which have several independent
// callers and can overlap: only the latest fetch settles error / loading /
// readiness, and a success commits only if no newer response already has.
let segmentsFetchGeneration = 0;
let segmentsCommittedGeneration = 0;
// The failed-load error a retry cleared, restored if that retry is aborted.
let segmentsRetryBaselineError: string | null = null;

// Fire the default-apply seed at most once per session.
let defaultsAppliedThisSession = false;

function isAbort(e: unknown): boolean {
  return (
    (typeof e === "object" && e !== null && (e as { name?: string }).name === "AbortError") ||
    (typeof e === "object" && e !== null && (e as { code?: string }).code === "ERR_CANCELED")
  );
}

/**
 * Fetches `GET /v2/segments` and hydrates `segmentsSlice` via
 * `setSegmentsCatalog`.
 */
export const fetchSegments = createAsyncThunk<
  SegmentListResponse | null,
  void,
  { rejectValue: { message: string; code?: string }; state: RootState }
>("segments/fetch", async (_, { dispatch, getState, rejectWithValue, signal }) => {
  const myGeneration = ++segmentsFetchGeneration;
  const isLatest = () => myGeneration === segmentsFetchGeneration;

  // Retrying after a failed initial load: drop the stale error and the
  // readiness it granted so consumers wait on this attempt.
  // `FetchFiltersAndSegments` re-marks ready once it succeeds (initialized)
  // or fails (error). The stale error is kept module-level so whichever
  // concurrent fetch settles last can restore it if that one is aborted.
  const { initialized, error } = getState().activeGroup.segments;
  if (!initialized && error != null) {
    segmentsRetryBaselineError = error;
    dispatch(setError(null));
    dispatch(resetSegmentsReady());
  }
  dispatch(setLoading(true));
  try {
    const response = await userService.segments.v2.list(signal);
    // Any successful response is valid catalog data — commit it unless a
    // newer one already landed — so a superseded caller that awaits this
    // fetch still sees a hydrated slice. Loading stays on while a newer
    // fetch is in flight.
    if (myGeneration < segmentsCommittedGeneration) return response;
    segmentsCommittedGeneration = myGeneration;
    segmentsRetryBaselineError = null;
    dispatch(setSegmentsCatalog(response));
    dispatch(setLoading(!isLatest()));
    // Seed default-apply: apply Age 18+ on first fetch if no segments applied
    try {
      const anyApplied = response.segments.some((s) => s.isApplied);
      if (!anyApplied && !defaultsAppliedThisSession) {
        const ageDefault = response.segments.find(
          (s) => s.type === 'SYSTEM' && /Age 18\+/i.test(s.label),
        );
        if (ageDefault) {
          defaultsAppliedThisSession = true;
          // Fire-and-forget: don't block the fetch resolution
          dispatch(setSegmentApplied({ code: ageDefault.code, isApplied: true }));
        }
      }
    } catch (err) {
      console.warn('[segments.thunk] default-apply seed failed', err);
    }
    return response;
  } catch (e) {
    // Only the latest fetch may settle error / loading / readiness; a
    // superseded one failing or aborting must not report over it.
    if (isAbort(e)) {
      if (isLatest()) {
        // A cancelled retry settles nothing: restore the previous failure so
        // readiness returns to its prior settled state instead of hanging.
        if (
          segmentsRetryBaselineError != null &&
          !getState().activeGroup.segments.initialized
        ) {
          dispatch(setError(segmentsRetryBaselineError));
        }
        segmentsRetryBaselineError = null;
        dispatch(setLoading(false));
      }
      return null;
    }
    const err = await normaliseSegmentError(e);
    if (isLatest()) {
      segmentsRetryBaselineError = null;
      dispatch(setError(err.message));
      dispatch(setLoading(false));
    }
    return rejectWithValue(err);
  }
});

const USER_SEGMENT_CAP = 200;
const TEMP_CODE_PREFIX = "__optimistic_";

/**
 * Hits `POST /v2/segments`. Optimistically inserts a temp-coded segment, then
 * swaps it for the server's response on success or rolls it back on failure.
 * Pre-checks the 200-USER-segment cap client-side to fail fast without a
 * round trip.
 */
export const createSegmentV2 = createAsyncThunk<
  SegmentV2,
  { label: string; categoryCode: string; selections: FilterSelection[] },
  { rejectValue: { message: string; code?: string }; state: RootState }
>("segments/create", async (input, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const active = Object.values(state.activeGroup.segments.segments.entities).filter(
    (s): s is SegmentV2 => !!s && s.type === "USER" && s.status === "ACTIVE",
  );
  if (active.length >= USER_SEGMENT_CAP) {
    ShowToaster.error(
      `You've reached the ${USER_SEGMENT_CAP}-segment limit. Delete one to add another.`
    );
    return rejectWithValue({ code: "SEGMENT_CAP_EXCEEDED", message: "Cap reached" });
  }

  const tempCode = `${TEMP_CODE_PREFIX}${Date.now()}`;
  const optimistic: SegmentV2 = {
    code: tempCode,
    label: input.label,
    categoryCode: input.categoryCode,
    type: "USER",
    status: "ACTIVE",
    color: null,
    // Matches the server's create response (no position yet), so the
    // placeholder lands at the end rather than flashing at the top.
    sortOrder: null,
    isApplied: false,
    markets: [],
    accessible: true,
    filters: [],
    updatedAt: new Date().toISOString(),
  };
  dispatch(optimisticInsertSegment(optimistic));

  try {
    const created = await userService.segments.v2.create({
      label: input.label,
      categoryCode: input.categoryCode,
      filters: toSegmentsFilterInputs(input.selections),
    });
    dispatch(optimisticRemoveSegment(tempCode));
    dispatch(optimisticInsertSegment(created));
    ShowToaster.success(`Segment "${created.label}" created`);
    return created;
  } catch (e) {
    dispatch(optimisticRemoveSegment(tempCode));
    const err = await normaliseSegmentError(e);
    dispatch(setError(err.message));
    ShowToaster.error(err.message);
    return rejectWithValue(err);
  }
});

/**
 * Hits `PATCH /v2/segments/:code`. Optimistically merges the requested
 * changes, then swaps in the server's response on success or rolls back to
 * the prior entity on failure.
 */
export const updateSegmentV2 = createAsyncThunk<
  SegmentV2,
  { code: string; label?: string; selections?: FilterSelection[] },
  { rejectValue: { message: string; code?: string }; state: RootState }
>("segments/update", async (input, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const previous = state.activeGroup.segments.segments.entities[input.code];
  if (!previous) return rejectWithValue({ code: "SEGMENT_NOT_FOUND", message: "Segment not in slice" });

  const optimisticChanges: Partial<SegmentV2> = {};
  if (input.label !== undefined) optimisticChanges.label = input.label;
  dispatch(optimisticUpdateSegment({ id: input.code, changes: optimisticChanges }));

  try {
    const body: SegmentPatchRequest = {};
    if (input.label !== undefined) body.label = input.label;
    if (input.selections !== undefined) body.filters = toSegmentsFilterInputs(input.selections);
    const updated = await userService.segments.v2.patchDefinition(input.code, body);
    dispatch(optimisticUpdateSegment({ id: input.code, changes: updated }));
    ShowToaster.success(`Segment updated`);
    return updated;
  } catch (e) {
    dispatch(optimisticUpdateSegment({ id: input.code, changes: previous }));
    const err = await normaliseSegmentError(e);
    ShowToaster.error(err.message);
    return rejectWithValue(err);
  }
});

/**
 * Hits `DELETE /v2/segments/:code`. Optimistically removes the entity, then
 * rolls it back on failure.
 */
export const deleteSegmentV2 = createAsyncThunk<
  string,
  string,
  { rejectValue: { message: string; code?: string }; state: RootState }
>("segments/delete", async (code, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const previous: SegmentV2 | undefined = state.activeGroup.segments.segments.entities[code];
  if (!previous) return rejectWithValue({ code: "SEGMENT_NOT_FOUND", message: "Segment not in slice" });

  dispatch(optimisticRemoveSegment(code));

  try {
    await userService.segments.v2.delete(code);
    ShowToaster.success(`Segment "${previous.label}" deleted`);
    return code;
  } catch (e) {
    dispatch(optimisticInsertSegment(previous));
    const err = await normaliseSegmentError(e);
    ShowToaster.error(err.message);
    return rejectWithValue(err);
  }
});

/**
 * Local-only bulk apply: sets `isApplied` to exactly `codes` in memory, for
 * showing a shared link's segments without persisting them. Never calls
 * `userService.segments.v2.patchPreference` — the caller is responsible for
 * restoring the persisted catalog state when the view-only session ends.
 */
export const applyLinkSegmentsLocal =
  (codes: string[]) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const target = new Set(codes);
    const entities = getState().activeGroup.segments.segments.entities;
    for (const id of Object.keys(entities)) {
      const shouldApply = target.has(id);
      if ((entities[id]?.isApplied ?? false) !== shouldApply) {
        dispatch(optimisticUpdateSegment({ id, changes: { isApplied: shouldApply } }));
      }
    }
  };

export const setSegmentApplied = createAsyncThunk<
  SegmentV2,
  { code: string; isApplied: boolean },
  { rejectValue: { message: string; code?: string }; state: RootState }
>("segments/setApplied", async ({ code, isApplied }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const previous = state.activeGroup.segments.segments.entities[code];
  if (!previous) return rejectWithValue({ code: "SEGMENT_NOT_FOUND", message: "Segment not in slice" });

  if (selectSmartLink(getState()).active) {
    // In-session edit while viewing a shared link: local only, no
    // patchPreference. Keep the session's linkSegments in sync so
    // `smartLinkReassert` doesn't revert this edit on the next catalog
    // reload (see smartLinkReassert.ts).
    dispatch(optimisticUpdateSegment({ id: code, changes: { isApplied } }));
    dispatch(setLinkSegments(selectAppliedSegmentCodes(getState())));
    return getState().activeGroup.segments.segments.entities[code] ?? previous;
  }

  dispatch(optimisticUpdateSegment({ id: code, changes: { isApplied } }));

  try {
    const updated = await userService.segments.v2.patchPreference(code, { isApplied });
    // Only merge the isApplied field from the server response — not the full
    // object — so a concurrent updateSegmentColor patchPreference that
    // resolves later cannot overwrite isApplied back to its pre-patch value.
    //
    // Skip the dispatch entirely when the server confirms the same value we
    // already applied optimistically. Dispatching an identical value still
    // triggers the appliedSegmentsMirror, which re-fires
    // setPendingSelectionIfChanged. If userSelection.segments hasn't been
    // updated yet by useURLToStore (the URL round-trip is async), the mirror
    // sees a stale [] and dispatches setPendingSelection a second time →
    // double screen load. Skipping the no-op dispatch eliminates that race.
    if (updated.isApplied !== isApplied) {
      dispatch(optimisticUpdateSegment({ id: code, changes: { isApplied: updated.isApplied } }));
    }
    return updated;
  } catch (e) {
    dispatch(optimisticUpdateSegment({ id: code, changes: { isApplied: previous.isApplied } }));
    const err = await normaliseSegmentError(e);
    ShowToaster.error(err.message);
    return rejectWithValue(err);
  }
});

export const updateSegmentColor = createAsyncThunk<
  SegmentV2,
  { code: string; color: string | null },
  { rejectValue: { message: string; code?: string }; state: RootState }
>("segments/updateColorV2", async ({ code, color }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const previous = state.activeGroup.segments.segments.entities[code];
  if (!previous) return rejectWithValue({ code: "SEGMENT_NOT_FOUND", message: "Segment not in slice" });

  if (selectSmartLink(getState()).active) {
    // In-session edit while viewing a shared link: local only, no
    // patchPreference.
    dispatch(optimisticUpdateSegment({ id: code, changes: { color } }));
    return getState().activeGroup.segments.segments.entities[code] ?? previous;
  }

  dispatch(optimisticUpdateSegment({ id: code, changes: { color } }));

  try {
    const updated = await userService.segments.v2.patchPreference(code, { color });
    // Only merge the color field from the server response — not the full
    // object — so a concurrent setSegmentApplied patchPreference that
    // resolves later cannot overwrite color back to its pre-patch value.
    //
    // Skip the dispatch when the server confirms the same value we already
    // applied optimistically — avoids a redundant appliedSegmentsMirror
    // trigger (same reasoning as the isApplied guard above).
    if (updated.color !== color) {
      dispatch(optimisticUpdateSegment({ id: code, changes: { color: updated.color } }));
    }
    return updated;
  } catch (e) {
    dispatch(optimisticUpdateSegment({ id: code, changes: { color: previous.color } }));
    const err = await normaliseSegmentError(e);
    ShowToaster.error(err.message);
    return rejectWithValue(err);
  }
});

/**
 * Hits `PATCH /v2/segments/:code/preference` once per code, in parallel.
 *
 * Generation-versioned: a new reorder call aborts any prior in-flight batch
 * and takes ownership of the slice. If any patch in the batch fails, the
 * slice is refetched from the server (no local rollback) so it always
 * reflects a real server state rather than a partially-applied one.
 */
export const updateSegmentsOrderV2 = createAsyncThunk<
  void,
  { orderedCodes: string[] },
  { rejectValue: { message: string; code?: string }; state: RootState }
>("segments/updateOrderV2", async ({ orderedCodes }, { dispatch, getState, rejectWithValue }) => {
  if (selectSmartLink(getState()).active) {
    // In-session edit while viewing a shared link: local only, no
    // patchPreference network calls.
    orderedCodes.forEach((code, index) => {
      dispatch(optimisticUpdateSegment({ id: code, changes: { sortOrder: index } }));
    });
    return;
  }

  // Abort any prior in-flight batch; increment generation
  segmentsReorderAbort?.abort("superseded-by-new-reorder");
  segmentsReorderAbort = new AbortController();
  const myGeneration = ++segmentsReorderGeneration;
  const signal = segmentsReorderAbort.signal;

  // Optimistic: apply the new sortOrder locally
  orderedCodes.forEach((code, index) => {
    dispatch(optimisticUpdateSegment({ id: code, changes: { sortOrder: index } }));
  });

  const results = await Promise.allSettled(
    orderedCodes.map(
      (code, index): Promise<SegmentV2> =>
        userService.segments.v2.patchPreference(code, { sortOrder: index }, signal),
    ),
  );

  // If a newer reorder superseded us, do nothing (that generation owns the state)
  if (myGeneration !== segmentsReorderGeneration) {
    return;
  }

  const hasFailure = results.some((r) => r.status === "rejected");
  if (hasFailure) {
    // Refetch and adopt server truth (no local rollback)
    ShowToaster.warning("Some updates failed — refreshed from server.");
    await dispatch(fetchSegments());
    return rejectWithValue({ code: "REORDER_PARTIAL_FAILURE", message: "Some patches failed" });
  }
});

/**
 * Hits `PATCH /v2/segment-categories/:code`. Optimistically applies the new
 * label, then swaps in the server's response on success or rolls back to the
 * prior entity on failure.
 */
export const updateCategoryNameV2 = createAsyncThunk<
  SegmentCategory,
  { code: string; label: string },
  { rejectValue: { message: string; code?: string }; state: RootState }
>("segments/updateCategoryNameV2", async ({ code, label }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const previous = state.activeGroup.segments.categories.entities[code];
  if (!previous) return rejectWithValue({ code: "SEGMENT_CATEGORY_NOT_FOUND", message: "Not in slice" });

  dispatch(optimisticUpdateCategory({ id: code, changes: { label } }));

  try {
    const updated = await userService.segments.v2.categories.patch(code, { label });
    dispatch(optimisticUpdateCategory({ id: code, changes: updated }));
    ShowToaster.success("Category renamed");
    return updated;
  } catch (e) {
    dispatch(optimisticUpdateCategory({ id: code, changes: { label: previous.label } }));
    const err = await normaliseSegmentError(e);
    ShowToaster.error(err.message);
    return rejectWithValue(err);
  }
});

/**
 * Hits `DELETE /v2/segment-categories/:code`. Optimistically removes the
 * entity, then rolls it back on failure. When `cascade` is set, the server
 * may also delete child segments, so a full refetch follows a successful
 * cascade delete to keep the segments slice in sync.
 */
export const deleteSegmentCategory = createAsyncThunk<
  string,
  { code: string; cascade?: boolean },
  { rejectValue: { message: string; code?: string }; state: RootState }
>("segments/deleteCategory", async ({ code, cascade }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const previous = state.activeGroup.segments.categories.entities[code];
  if (!previous) return rejectWithValue({ code: "SEGMENT_CATEGORY_NOT_FOUND", message: "Not in slice" });

  dispatch(optimisticRemoveCategory(code));

  try {
    await userService.segments.v2.categories.delete(code, cascade);
    ShowToaster.success("Category deleted");
    await dispatch(fetchSegments());
    return code;
  } catch (e) {
    dispatch(optimisticInsertCategory(previous));
    const err = await normaliseSegmentError(e);
    ShowToaster.error(err.message);
    return rejectWithValue(err);
  }
});

// Module-level generation counter and abort controller for category reorder
// batches — mirrors the segment-reorder pattern above: a newer reorder
// supersedes (aborts) an older in-flight one, and only the latest generation
// is allowed to commit its result to the slice.
let categoryReorderGeneration = 0;
let categoryReorderAbort: AbortController | null = null;

/**
 * Hits `PATCH /v2/segment-categories/:code` once per code, in parallel.
 *
 * Generation-versioned: a new reorder call aborts any prior in-flight batch
 * and takes ownership of the slice. If any patch in the batch fails, the
 * slice is refetched from the server (no local rollback) so it always
 * reflects a real server state rather than a partially-applied one.
 */
export const updateCategoryOrderV2 = createAsyncThunk<
  void,
  { orderedCodes: string[] },
  { rejectValue: { message: string; code?: string }; state: RootState }
>("segments/updateCategoryOrderV2", async ({ orderedCodes }, { dispatch, rejectWithValue }) => {
  categoryReorderAbort?.abort("superseded-by-new-reorder");
  categoryReorderAbort = new AbortController();
  const myGeneration = ++categoryReorderGeneration;
  const signal = categoryReorderAbort.signal;

  orderedCodes.forEach((code, index) => {
    dispatch(optimisticUpdateCategory({ id: code, changes: { sortOrder: index } }));
  });

  const results = await Promise.allSettled(
    orderedCodes.map(
      (code, index): Promise<SegmentCategory> =>
        userService.segments.v2.categories.patch(code, { sortOrder: index }, signal),
    ),
  );

  if (myGeneration !== categoryReorderGeneration) {
    return;
  }

  const hasFailure = results.some((r) => r.status === "rejected");
  if (hasFailure) {
    ShowToaster.warning("Some category-order updates failed — refreshed from server.");
    await dispatch(fetchSegments());
    return rejectWithValue({ code: "REORDER_PARTIAL_FAILURE", message: "Some patches failed" });
  }
});
